import * as Styled from "./style";
import type { Maybe, G_VideoViewingType, G_GetVideoQuery, G_VideoSeasonType } from "~/lib/graphql";
import { formatDateToYMDDayHm } from "~/util/format-date";
import { getTagText } from "~/util/get-tag-text";

type DeliveryTagProps = {
  /** DMMTVの独占作品かどうかを判定するフラグ */
  isExclusive: boolean;
  /** 新着作品かどうかを判定するフラグ */
  isNewArrival: boolean;
  /** カスタムタグ */
  customTag?: G_GetVideoQuery["video"]["customTag"];
  /** 販売中かどうか（undefinedの時は早期returnしたとき） */
  isOnSale: boolean | undefined;
  /** 作品の次回配信開始日(SVOD PPVの判断はBEがよしなに行う) */
  nextEpisodeStartDeliveryAt: Maybe<Date> | undefined;
  /** 次回エピソード配信日の配信前フラグ */
  isNextEpisodeBeforeDelivered: boolean;
  /** 配信前かつ公開されている場合に`true`になる */
  isPublicBeingDelivered: boolean;
  /** PREMIUM | PPV | FREE */
  viewingTypes: G_VideoViewingType[];
  /** シーズン種別 */
  seasonType: G_VideoSeasonType;
};
/** DMMでの取扱情報タグの「配信日」の表示の出し分けを行うコンポーネント */
export const DeliveryTag = (props: DeliveryTagProps) => {
  const {
    isExclusive,
    isNewArrival,
    customTag,
    nextEpisodeStartDeliveryAt,
    isNextEpisodeBeforeDelivered,
    isPublicBeingDelivered,
    seasonType,
  } = props;
  // 併売時はここの条件式を通過させない。PPV販売の時のみここの条件式を通過させる必要がある
  // MEMO（ishihara）: BEからエラーが帰ってくるので今はコメントアウト
  // if (viewingTypes.length === 1 && viewingTypes[0] === G_VideoViewingType.Ppv) {
  //   if (isOnSale !== undefined) {
  //     if (!isOnSale) return <Styled.ParDeliveryText>販売は終了しました</Styled.ParDeliveryText>;
  //   }
  // }

  // 次回配信エピソードの日付が登録されている場合の条件式
  if (nextEpisodeStartDeliveryAt !== undefined && nextEpisodeStartDeliveryAt !== null) {
    /** YYYY.MM.DD(W) HH:MM + 配信開始 の文字列 */
    const subText = `${formatDateToYMDDayHm(nextEpisodeStartDeliveryAt)} 配信開始`;
    if (isNextEpisodeBeforeDelivered) {
      // 配信前作品（isPublicBeingDeliveredがtrue）の場合
      if (isPublicBeingDelivered) {
        if (customTag) {
          return (
            <Styled.DivFlexContainer>
              <Styled.CustomStyleTag tagText={customTag} bgColor="RED900" />
              <Styled.CustomStyleTag tagText="配信前" subText={subText} borderColor="GRAY900" color="GRAY900" />
            </Styled.DivFlexContainer>
          );
        }
        // 独占でも新着でもない場合 : 配信前タグ + 配信開始日を表示させること
        if (!isExclusive && !isNewArrival) {
          return <Styled.CustomStyleTag tagText="配信前" subText={subText} borderColor="GRAY900" color="GRAY900" />;
        }
        // 独占作品の場合 : 独占タグ + 配信前タグ + 配信開始日を表示させること
        if (isExclusive && !isNewArrival) {
          return (
            <Styled.DivFlexContainer>
              <Styled.CustomStyleTag tagText="独占" bgColor="RED900" />
              <Styled.CustomStyleTag tagText="配信前" subText={subText} borderColor="GRAY900" color="GRAY900" />
            </Styled.DivFlexContainer>
          );
        }
      } else {
        // 配信前作品でない（isPublicBeingDeliveredがfalse）の場合
        const tagText = getTagText({ isNewArrival, isExclusive, customTag });

        if (tagText) {
          return <Styled.CustomStyleTag tagText={tagText} subText={subText} bgColor="RED900" />;
        }

        return <Styled.ParDeliveryText>{subText}</Styled.ParDeliveryText>;
      }
    } else {
      /** 次回 + YYYY.MM.DD(W) HH:MM + 配信開始 の文字列 */
      const nextSubText = `次回 ${formatDateToYMDDayHm(nextEpisodeStartDeliveryAt)} 配信開始`;
      const tagText = getTagText({ isNewArrival, isExclusive, customTag });

      if (tagText) {
        return <Styled.CustomStyleTag tagText={tagText} subText={nextSubText} bgColor="RED900" />;
      }

      return <Styled.ParDeliveryText>{nextSubText}</Styled.ParDeliveryText>;
    }
  }

  const tagText = getTagText({ isNewArrival, isExclusive, customTag, seasonType });
  if (tagText === null) return null;

  return <Styled.CustomStyleTag tagText={tagText} bgColor="RED900" />;
};
